/* useBkrLive — SSE-hook met poll-fallback.
 * Retourneert de laatste BKR-snapshot ({datum, items, ...}) en re-connect
 * automatisch bij connectiefout. Verifieert eerst of EventSource beschikbaar
 * is; op oudere/embedded browsers valt hij terug op een 60s polling-loop. */
import { useEffect, useRef, useState } from "react";
import api from "@/lib/api";

export default function useBkrLive({ intervalSec = 30 } = {}) {
  const [snapshot, setSnapshot] = useState(null);
  const [error, setError] = useState(null);
  const [streamMode, setStreamMode] = useState("init");
  const esRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    let pollTimer = null;

    const startPoll = () => {
      setStreamMode("poll");
      const tick = async () => {
        if (cancelled) return;
        if (document.visibilityState !== "visible") return;
        try {
          const r = await api.get("/bkr/live");
          setSnapshot(r.data);
          setError(null);
        } catch (e) { setError(e.message); }
      };
      tick();
      pollTimer = window.setInterval(tick, Math.max(15, intervalSec) * 1000);
    };

    const startSse = () => {
      if (typeof window.EventSource === "undefined") {
        startPoll();
        return;
      }
      const base = process.env.REACT_APP_BACKEND_URL || "";
      const url = `${base}/api/bkr/live/stream?interval=${intervalSec}`;
      let es;
      try {
        // withCredentials: true zodat cookie-based auth wordt meegestuurd
        es = new EventSource(url, { withCredentials: true });
      } catch {
        startPoll();
        return;
      }
      esRef.current = es;
      setStreamMode("sse");
      es.addEventListener("bkr", (evt) => {
        try {
          const data = JSON.parse(evt.data);
          if (!cancelled) { setSnapshot(data); setError(null); }
        } catch {/* ignore parse */}
      });
      es.addEventListener("close", () => {
        // Server sluit netjes na 30 min — reconnect
        es.close();
        if (!cancelled) startSse();
      });
      es.onerror = () => {
        es.close();
        esRef.current = null;
        if (cancelled) return;
        // Bij fatal error: val terug op polling
        setError("SSE-verbinding gesloten — polling actief");
        startPoll();
      };
    };

    startSse();

    return () => {
      cancelled = true;
      if (esRef.current) { try { esRef.current.close(); } catch {} }
      if (pollTimer) window.clearInterval(pollTimer);
    };
  }, [intervalSec]);

  return { snapshot, error, streamMode };
}
